import React, { useState, useRef } from 'react';
import { 
  User, Mail, Phone, MapPin, Camera, Save, Shield, 
  Lock, Bell, History, Smartphone, CheckCircle2, AlertCircle, 
  Building, BadgeCheck, ArrowLeft, X, Image as ImageIcon, Upload
} from 'lucide-react';

interface AdminProfileProps {
  onNavigate: (page: string) => void;
}

const AdminProfile: React.FC<AdminProfileProps> = ({ onNavigate }) => {
  const [isSaving, setIsSaving] = useState(false);
  
  // Profile Photo Upload State
  const [showPhotoUploadModal, setShowPhotoUploadModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Form State
  const [profile, setProfile] = useState({
    firstName: 'Administrator',
    lastName: 'User',
    email: 'rsgleamofgolds@gmail.com',
    phone: '+63 917 123 4567',
    role: 'Senior Gemologist',
    department: 'Inventory & Vault',
    employeeId: 'SG-8829-ADM',
    location: 'Makati Atelier',
    bio: 'Specializing in rare diamond procurement and vintage restoration. Overseeing digital inventory operations since 2018.',
    avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=1000&auto=format&fit=crop'
  });

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    twoFactor: true,
    emailNotifs: true,
    smsNotifs: false
  });

  // Mock Activity Log
  const activities = [
    { action: 'Updated Gold Rates', date: 'Today, 10:23 AM', device: 'Desktop - Chrome' },
    { action: 'Added Product: Aurora Necklace', date: 'Yesterday, 4:45 PM', device: 'Desktop - Chrome' },
    { action: 'Exported Monthly Report', date: 'Oct 24, 2023', device: 'Tablet - Safari' },
    { action: 'Password Changed', date: 'Oct 15, 2023', device: 'Mobile - iOS' },
  ];

  // Profile Photo Upload Handlers
  const handlePhotoClick = () => {
    setShowPhotoUploadModal(true);
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoUpload = () => {
    if (selectedPhoto) {
      setIsUploadingPhoto(true);
      
      // Simulate upload delay
      setTimeout(() => {
        setProfile(prev => ({ ...prev, avatar: selectedPhoto }));
        setSelectedPhoto(null);
        setShowPhotoUploadModal(false);
        setIsUploadingPhoto(false);
        alert("Profile photo updated successfully!");
      }, 1500);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setTimeout(() => {
      setIsSaving(false);
      alert("Profile updated successfully.");
    }, 1500);
  };

  return (
    <div className="pt-44 pb-12 min-h-screen bg-stone-50">
      <div className="max-w-6xl mx-auto px-6">
        
        {/* Profile Photo Upload Modal */}
        {showPhotoUploadModal && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center">
            <div className="absolute inset-0 bg-stone-900/70 backdrop-blur-sm" onClick={() => setShowPhotoUploadModal(false)}></div>
            <div className="relative bg-white w-full max-w-md rounded-sm shadow-2xl animate-fade-up-enter fade-up-active mx-4 overflow-hidden">
              <div className="bg-stone-900 text-white p-6 flex justify-between items-center">
                <div>
                  <h3 className="font-serif text-xl">Update Profile Photo</h3>
                  <p className="text-xs text-stone-400">Choose a new profile picture</p>
                </div>
                <button onClick={() => setShowPhotoUploadModal(false)} className="text-stone-400 hover:text-white">
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-8 space-y-6">
                {/* Hidden file input */}
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handlePhotoSelect}
                  accept="image/*"
                  className="hidden"
                />
                
                {/* Preview Area */}
                <div className="flex flex-col items-center">
                  <div className="w-40 h-40 rounded-full border-4 border-gold-200 overflow-hidden shadow-xl mb-4">
                    <img 
                      src={selectedPhoto || profile.avatar} 
                      alt="Profile Preview" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {selectedPhoto ? (
                    <p className="text-xs text-green-600 font-bold">New photo selected</p>
                  ) : (
                    <p className="text-xs text-stone-400">Current profile photo</p>
                  )}
                </div>
                
                {/* Action Buttons */}
                <div className="flex flex-col gap-3">
                  <button 
                    onClick={triggerFileInput}
                    className="w-full border-2 border-dashed border-stone-300 py-4 px-4 rounded-sm hover:border-gold-500 hover:bg-stone-50 transition-colors flex items-center justify-center gap-2"
                  >
                    <Camera size={18} className="text-stone-400" />
                    <span className="text-sm font-medium">Choose New Photo</span>
                  </button>
                  
                  <div className="flex gap-3">
                    <button 
                      onClick={() => setShowPhotoUploadModal(false)}
                      className="flex-1 border border-stone-300 text-stone-600 py-3 text-xs uppercase tracking-widest hover:bg-stone-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handlePhotoUpload}
                      disabled={!selectedPhoto || isUploadingPhoto}
                      className="flex-1 bg-stone-900 text-white py-3 text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isUploadingPhoto ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Uploading...
                        </>
                      ) : (
                        'Upload Photo'
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div>
                 <button onClick={() => onNavigate('admin-dashboard')} className="flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors">
                    <ArrowLeft size={14} /> Back to Dashboard
                 </button>
                <h1 className="font-serif text-3xl text-stone-900">Admin Profile</h1>
                <p className="text-stone-500 font-light mt-2 text-sm">Manage your credentials, preferences, and personal information.</p>
            </div>
            
            <button 
                onClick={handleSave}
                disabled={isSaving}
                className="px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors flex items-center gap-2 shadow-lg disabled:opacity-70"
            >
                {isSaving ? 'Saving...' : 'Save Changes'}
                <Save size={16} />
            </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Left Column: Profile Card */}
            <div className="lg:col-span-1 space-y-8">
                
                {/* ID Card */}
                <div className="bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden relative">
                    <div className="h-32 bg-stone-900 relative overflow-hidden">
                        <div className="absolute inset-0 opacity-30 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
                        <div className="absolute inset-0 bg-gradient-to-r from-gold-600/20 to-transparent"></div>
                    </div>
                    
                    <div className="px-6 pb-6 text-center -mt-16 relative">
                        <div 
                          className="relative inline-block group cursor-pointer"
                          onClick={handlePhotoClick}
                        >
                            <div className="w-32 h-32 rounded-full border-4 border-white bg-stone-200 overflow-hidden shadow-lg mx-auto">
                                <img 
                                    src={profile.avatar} 
                                    alt="Admin Profile" 
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                            </div>
                            <div className="absolute bottom-1 right-1 bg-stone-900 text-white p-2 rounded-full hover:bg-gold-600 transition-colors shadow-md">
                                <Camera size={16} />
                            </div>
                        </div>
                        
                        <h2 className="font-serif text-2xl text-stone-900 mt-4">{profile.firstName} {profile.lastName}</h2>
                        <p className="text-gold-600 text-xs uppercase tracking-widest font-bold mb-1">{profile.role}</p>
                        <p className="text-stone-400 text-xs flex items-center justify-center gap-1">
                            <Building size={12} /> {profile.department}
                        </p>

                        <div className="mt-6 flex justify-center gap-2">
                             <span className="bg-emerald-50 text-emerald-700 px-3 py-1 rounded-full text-[10px] uppercase font-bold border border-emerald-100 flex items-center gap-1">
                                <CheckCircle2 size={12} /> Active Status
                             </span>
                             <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-[10px] uppercase font-bold border border-blue-100 flex items-center gap-1">
                                <BadgeCheck size={12} /> Verified
                             </span>
                        </div>
                    </div>

                    <div className="border-t border-stone-100 p-6 bg-stone-50/50">
                        <div className="flex justify-between items-center text-sm mb-2">
                            <span className="text-stone-500">Employee ID</span>
                            <span className="font-mono text-stone-900">{profile.employeeId}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                            <span className="text-stone-500">Maison Location</span>
                            <span className="text-stone-900">{profile.location}</span>
                        </div>
                    </div>
                </div>

                {/* Contact Quick View */}
                <div className="bg-white border border-stone-200 shadow-sm rounded-sm p-6">
                    <h3 className="text-xs uppercase tracking-widest text-stone-400 font-bold mb-4">Contact Information</h3>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-stone-50 flex items-center justify-center text-gold-600">
                                <Mail size={16} />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase text-stone-400">Email Address</p>
                                <p className="text-sm text-stone-900">{profile.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-stone-50 flex items-center justify-center text-gold-600">
                                <Phone size={16} />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase text-stone-400">Phone Number</p>
                                <p className="text-sm text-stone-900">{profile.phone}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-stone-50 flex items-center justify-center text-gold-600">
                                <MapPin size={16} />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase text-stone-400">Location</p>
                                <p className="text-sm text-stone-900">{profile.location}</p>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Right Column: Settings Forms */}
            <div className="lg:col-span-2 space-y-8">
                
                {/* Personal Details Form */}
                <div className="bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-stone-100 bg-stone-50/30 flex justify-between items-center">
                        <h3 className="font-serif text-lg text-stone-900">Personal Details</h3>
                        <User size={16} className="text-stone-400" />
                    </div>
                    <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">First Name</label>
                            <input 
                                type="text" 
                                value={profile.firstName}
                                onChange={(e) => setProfile({...profile, firstName: e.target.value})}
                                className="w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Last Name</label>
                            <input 
                                type="text" 
                                value={profile.lastName}
                                onChange={(e) => setProfile({...profile, lastName: e.target.value})}
                                className="w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white"
                            />
                        </div>

                        {/* ADDED CONTACT INPUTS */}
                        <div className="space-y-2">
                            <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Email Address</label>
                            <input 
                                type="email" 
                                value={profile.email}
                                onChange={(e) => setProfile({...profile, email: e.target.value})}
                                className="w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Phone Number</label>
                            <input 
                                type="text" 
                                value={profile.phone}
                                onChange={(e) => setProfile({...profile, phone: e.target.value})}
                                className="w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Official Title</label>
                            <input 
                                type="text" 
                                value={profile.role}
                                onChange={(e) => setProfile({...profile, role: e.target.value})}
                                className="w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Department</label>
                            <select 
                                value={profile.department}
                                onChange={(e) => setProfile({...profile, department: e.target.value})}
                                className="w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white"
                            >
                                <option>Inventory & Vault</option>
                                <option>Sales & Concierge</option>
                                <option>Executive Management</option>
                                <option>Logistics</option>
                            </select>
                        </div>

                        <div className="space-y-2 md:col-span-2">
                            <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Location / Office</label>
                            <input 
                                type="text" 
                                value={profile.location}
                                onChange={(e) => setProfile({...profile, location: e.target.value})}
                                className="w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white"
                            />
                        </div>

                        <div className="md:col-span-2 space-y-2">
                             <label className="text-xs uppercase tracking-widest text-stone-500 font-bold">Professional Bio</label>
                             <textarea 
                                rows={3}
                                value={profile.bio}
                                onChange={(e) => setProfile({...profile, bio: e.target.value})}
                                className="w-full border border-stone-300 px-4 py-2.5 text-sm focus:border-gold-500 focus:ring-1 focus:ring-gold-500 focus:outline-none rounded-sm bg-white resize-none"
                             />
                             <p className="text-[10px] text-stone-400 text-right">Visible to internal staff only.</p>
                        </div>
                    </div>
                </div>

                {/* Security Section */}
                <div className="bg-white border border-stone-200 shadow-sm rounded-sm overflow-hidden">
                    <div className="px-6 py-4 border-b border-stone-100 bg-stone-50/30 flex justify-between items-center">
                        <h3 className="font-serif text-lg text-stone-900">Security & Access</h3>
                        <Shield size={16} className="text-stone-400" />
                    </div>
                    <div className="p-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                             <div className="space-y-4">
                                <h4 className="text-sm font-bold text-stone-900 flex items-center gap-2">
                                    <Lock size={14} className="text-gold-600"/> Change Password
                                </h4>
                                <input 
                                    type="password" 
                                    placeholder="Current Password"
                                    className="w-full border border-stone-300 px-4 py-2 text-sm focus:border-gold-500 focus:outline-none rounded-sm"
                                />
                                <input 
                                    type="password" 
                                    placeholder="New Password"
                                    className="w-full border border-stone-300 px-4 py-2 text-sm focus:border-gold-500 focus:outline-none rounded-sm"
                                />
                                <input 
                                    type="password" 
                                    placeholder="Confirm New Password"
                                    className="w-full border border-stone-300 px-4 py-2 text-sm focus:border-gold-500 focus:outline-none rounded-sm"
                                />
                             </div>
                             
                             <div className="space-y-4">
                                <h4 className="text-sm font-bold text-stone-900 flex items-center gap-2">
                                    <Smartphone size={14} className="text-gold-600"/> Two-Factor Authentication
                                </h4>
                                <div className="bg-stone-50 p-4 rounded-sm border border-stone-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-stone-800">Authenticator App</span>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input type="checkbox" checked={security.twoFactor} onChange={(e) => setSecurity({...security, twoFactor: e.target.checked})} className="sr-only peer" />
                                            <div className="w-9 h-5 bg-stone-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-gold-600"></div>
                                        </label>
                                    </div>
                                    <p className="text-xs text-stone-500 leading-relaxed">
                                        Secure your account by requiring a code from your mobile device when logging in.
                                    </p>
                                </div>

                                <h4 className="text-sm font-bold text-stone-900 flex items-center gap-2 mt-6">
                                    <Bell size={14} className="text-gold-600"/> Notifications
                                </h4>
                                <div className="space-y-2">
                                    <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer">
                                        <input type="checkbox" checked={security.emailNotifs} onChange={(e) => setSecurity({...security, emailNotifs: e.target.checked})} className="accent-gold-600" />
                                        Email Alerts for Login
                                    </label>
                                    <label className="flex items-center gap-2 text-sm text-stone-600 cursor-pointer">
                                        <input type="checkbox" checked={security.smsNotifs} onChange={(e) => setSecurity({...security, smsNotifs: e.target.checked})} className="accent-gold-600" />
                                        SMS Alerts for High Value Orders
                                    </label>
                                </div>
                             </div>
                        </div>

                        <div className="border-t border-stone-100 pt-6">
                            <h4 className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-4 flex items-center gap-2">
                                <History size={14} /> Recent Session Activity
                            </h4>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-stone-100 text-[10px] uppercase text-stone-500">
                                        <tr>
                                            <th className="px-4 py-2">Action</th>
                                            <th className="px-4 py-2">Date & Time</th>
                                            <th className="px-4 py-2">Device</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-stone-50">
                                        {activities.map((act, idx) => (
                                            <tr key={idx} className="hover:bg-stone-50">
                                                <td className="px-4 py-3 text-xs font-medium text-stone-900">{act.action}</td>
                                                <td className="px-4 py-3 text-xs text-stone-500">{act.date}</td>
                                                <td className="px-4 py-3 text-xs text-stone-500 font-mono">{act.device}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;